/**
 * AiAnalysisV2 — Detailed AI analysis view for V2 signal data.
 * Works for SPX, SPY, ES, MES by passing the appropriate API path.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import '../Dashboard.css';

const API = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const c = {
  bg: '#0f172a', card: '#1f2937', cardAlt: '#1a2332', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280',
  green: '#16a34a', blue: '#3b82f6', amber: '#d97706', red: '#dc2626', purple: '#8b5cf6',
};

const fmt = (n: any, d = 2) => (n == null ? '—' : Number(n).toFixed(d));
const fmtDate = (s: string) => { try { return new Date(s).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return s || '—'; } };

interface Props {
  apiPath: string;           // e.g. '/spx/v2' or '/indices/v2?symbol=SPY'
  title: string;             // e.g. 'SPX AI Analysis'
  symbol: string;            // e.g. 'SPX'
}

const HORIZON_LABELS: Record<number, string> = {
  0: '0 DTE (Intraday)', 1: '1 DTE (Overnight)', 7: '7 DTE (Weekly)',
  14: '14 DTE (Bi-weekly)', 21: '21 DTE (3-week)', 30: '30 DTE (Monthly)',
  45: '45 DTE (Standard)', 60: '60 DTE (Extended)',
};

const SYMBOL_PRODUCTS: Record<string, { flag: string; legacy?: string; price: string; color: string; label: string }> = {
  SPX:  { flag: 'spxSignalsEnabled', price: '$790/yr', color: '#6d28d9', label: 'SPX Signals' },
  SPY:  { flag: 'spySignalsEnabled', legacy: 'indexSignalsEnabled', price: '$490/yr', color: '#0ea5e9', label: 'SPY Signals' },
  ES:   { flag: 'esSignalsEnabled', legacy: 'indexSignalsEnabled', price: '$790/yr', color: '#d97706', label: 'ES Signals' },
  MES:  { flag: 'mesSignalsEnabled', legacy: 'indexSignalsEnabled', price: '$490/yr', color: '#16a34a', label: 'MES Signals' },
};
// Map page symbol to which products to check/show (ES page should also show MES)
const SYMBOL_GROUP: Record<string, string[]> = {
  SPX: ['SPX'], SPY: ['SPY'], ES: ['ES', 'MES'], MES: ['ES', 'MES'],
};

export const AiAnalysisV2: React.FC<Props> = ({ apiPath, title, symbol }) => {
  const { user, token } = useAuth();
  const [signal, setSignal] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Gate: check product-specific access (computed before hooks, rendered after)
  const gateSymbols = SYMBOL_GROUP[symbol] || [symbol];
  const hasAccess = gateSymbols.some(s => {
    const p = SYMBOL_PRODUCTS[s];
    return p && !!((user as any)?.[p.flag] || (p.legacy && (user as any)?.[p.legacy]));
  });

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      const sep = apiPath.includes('?') ? '&' : '?';
      let r = await fetch(`${API}${apiPath}${sep}date=${today}`, { headers: { Authorization: `Bearer ${token}` } });
      let data = await r.json();

      // Fall back to yesterday if no data today
      if (!data.signals?.length) {
        const yesterday = new Date(Date.now() - 86400000).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        r = await fetch(`${API}${apiPath}${sep}date=${yesterday}`, { headers: { Authorization: `Bearer ${token}` } });
        data = await r.json();
      }

      if (data.signals?.length > 0) setSignal(data.signals[0]);
      else if (data.signal) setSignal(data.signal);
      else setError('No signal data available for today or yesterday');
    } catch { setError('Failed to load signal data'); }
    finally { setLoading(false); }
  }, [token, apiPath]);

  useEffect(() => { load(); }, [load]);

  const renderAiSection = (key: string, analysis: any, label: string, accentColor: string) => {
    if (!analysis) return null;

    const horizons = signal?.horizons || {};
    const sortedDtes = Object.keys(horizons).map(Number).sort((a, b) => a - b);

    // Get per-horizon picks, enriched with candidate metrics
    const getPickForHorizon = (targetDte: number) => {
      const bph = analysis.best_picks_by_horizon || {};
      const actualDte = horizons[targetDte]?.actual_dte;
      let pick = bph[String(targetDte)] || bph[String(actualDte)] || null;
      if (!pick) {
        const picks = analysis.top_picks || [];
        pick = picks.find((p: any) => p.horizon_dte === targetDte || p.dte === actualDte) || null;
      }
      if (!pick) return null;

      // Enrich with full metrics from our model's candidates (match by strike)
      const candidates = horizons[targetDte]?.candidates || [];
      const strike = Number(pick.strike);
      const match = candidates.find((c: any) => Number(c.strike) === strike);
      if (match) return { ...match, ...pick, reasoning: pick.reasoning };
      return pick;
    };

    return (
      <div key={key} style={{ marginBottom: '2rem' }}>
        {/* AI Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: `${accentColor}15`, border: `1px solid ${accentColor}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800, color: accentColor }}>
            {key === 'claude' ? 'CL' : 'GP'}
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', color: '#f59e0b' }}>{label}</h3>
            <span style={{ fontSize: '0.75rem', color: c.dim }}>Independent analysis — does not see our model's rankings</span>
          </div>
        </div>

        {/* Market Context */}
        {analysis.market_context && (
          <div style={{ background: c.cardAlt, borderRadius: 8, padding: '1rem', marginBottom: '1rem', borderLeft: `3px solid ${accentColor}` }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', color: accentColor, marginBottom: '0.4rem' }}>MARKET CONTEXT</div>
            <p style={{ fontSize: '0.9rem', color: c.text, lineHeight: 1.7, margin: 0 }}>{analysis.market_context}</p>
          </div>
        )}

        {/* Summary */}
        {analysis.summary && (
          <div style={{ background: c.cardAlt, borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', color: c.muted, marginBottom: '0.4rem' }}>ANALYSIS SUMMARY</div>
            <p style={{ fontSize: '0.9rem', color: c.text, lineHeight: 1.7, margin: 0, whiteSpace: 'pre-line' }}>{analysis.summary}</p>
          </div>
        )}

        {/* Per-Horizon Picks */}
        {sortedDtes.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', color: c.muted, marginBottom: '0.75rem' }}>PICKS BY TIME HORIZON</div>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {sortedDtes.map(dte => {
                const pick = getPickForHorizon(dte);
                if (!pick) return null;
                const hLabel = HORIZON_LABELS[dte] || `${dte} DTE`;
                return (
                  <div key={dte} style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '1rem' }}>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: accentColor, marginBottom: '0.5rem' }}>{hLabel}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.82rem' }}>
                        <span><span style={{ color: c.muted }}>Strike </span><span style={{ fontWeight: 700 }}>{pick.strike}</span></span>
                        <span><span style={{ color: c.muted }}>Credit </span><span style={{ fontWeight: 700 }}>${fmt(pick.credit || pick.mid_price)}</span></span>
                        {pick.prob_profit_pct != null && <span><span style={{ color: c.muted }}>Win </span><span style={{ fontWeight: 700 }}>{fmt(pick.prob_profit_pct, 1)}%</span></span>}
                        {pick.delta != null && <span><span style={{ color: c.muted }}>Delta </span><span style={{ fontWeight: 700 }}>{fmt(pick.delta, 3)}</span></span>}
                        {pick.safety_cushion_pct != null && <span><span style={{ color: c.muted }}>Safety </span><span style={{ fontWeight: 700 }}>{fmt(pick.safety_cushion_pct, 1)}%</span></span>}
                      </div>
                    </div>
                    {pick.reasoning && (
                      <p style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-line' }}>{pick.reasoning}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Risk Warnings */}
        {analysis.risk_warnings?.length > 0 && (
          <div style={{ background: `${c.amber}10`, border: `1px solid ${c.amber}30`, borderRadius: 8, padding: '1rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', color: c.amber, marginBottom: '0.5rem' }}>RISK WARNINGS</div>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.85rem', color: c.text }}>
              {analysis.risk_warnings.map((w: string, i: number) => (
                <li key={i} style={{ marginBottom: '0.3rem', lineHeight: 1.6 }}>{w}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (!hasAccess) {
    const missingProducts = gateSymbols.filter(s => {
      const p = SYMBOL_PRODUCTS[s];
      return p && !((user as any)?.[p.flag] || (p.legacy && (user as any)?.[p.legacy]));
    });
    return (
      <div className="dashboard-container">
        <Navigation />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center', color: c.text }}>
          <h2 style={{ marginBottom: '0.75rem' }}>{title}</h2>
          <p style={{ color: c.muted, maxWidth: 500, margin: '0 auto 1.5rem' }}>
            Independent AI analysis using Claude and OpenAI for put-selling signals — ranked picks, risk assessment, and market reasoning.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {missingProducts.map(s => {
              const p = SYMBOL_PRODUCTS[s];
              return p ? (
                <a key={s} href="/upgrade" style={{ display: 'inline-block', backgroundColor: p.color, color: 'white', padding: '0.75rem 1.5rem', textDecoration: 'none', borderRadius: 8, fontWeight: 'bold', fontSize: 14 }}>
                  {p.label} — {p.price}
                </a>
              ) : null;
            })}
          </div>
          <p style={{ color: c.dim, fontSize: 13, marginTop: 12 }}>
            Or get everything with the <a href="/upgrade" style={{ color: '#a78bfa' }}>Bundle — $1,990/yr</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '1.5rem', color: c.text }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{title}</h1>
          <button onClick={() => load()} style={{
            padding: '0.5rem 1rem', borderRadius: 6, border: `1px solid ${c.border}`,
            background: 'transparent', color: c.muted, cursor: 'pointer', fontSize: '0.82rem',
          }}>Refresh</button>
        </div>

        {/* Signal info */}
        {signal && (
          <div style={{ color: c.muted, fontSize: '0.85rem', marginBottom: '1.5rem' }}>
            {symbol} Spot: {fmt(signal.spot_price)} | Generated: {fmtDate(signal.generated_at)} | Candidates: {signal.total_candidates || '—'}
          </div>
        )}

        {loading && <div style={{ textAlign: 'center', padding: '3rem', color: c.muted }}>Loading AI analysis...</div>}
        {error && <div style={{ textAlign: 'center', padding: '3rem', color: c.red }}>{error}</div>}

        {!loading && signal && (
          <>
            {/* Confidence Overview */}
            {signal.claude_analysis && signal.chatgpt_analysis && (
              <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '1rem', marginBottom: '2rem', textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', color: c.muted, marginBottom: '0.5rem' }}>TRIPLE VALIDATION</div>
                <p style={{ fontSize: '0.9rem', color: '#cbd5e1', margin: '0 auto 1rem', maxWidth: 500, lineHeight: 1.6 }}>
                  Our quantitative model, Claude AI, and ChatGPT independently analyze the same market data.
                  When all three converge on the same pick — that's a high-confidence signal.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.4rem', fontSize: '0.85rem', fontWeight: 800, color: '#f59e0b' }}>QM</div>
                    <div style={{ fontSize: '0.75rem', color: c.muted }}>Our Model</div>
                  </div>
                  <div>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.4rem', fontSize: '0.85rem', fontWeight: 800, color: '#60a5fa' }}>CL</div>
                    <div style={{ fontSize: '0.75rem', color: c.muted }}>Claude AI</div>
                  </div>
                  <div>
                    <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.4rem', fontSize: '0.85rem', fontWeight: 800, color: '#34d399' }}>GP</div>
                    <div style={{ fontSize: '0.75rem', color: c.muted }}>ChatGPT</div>
                  </div>
                </div>
              </div>
            )}

            {/* Claude Analysis */}
            {renderAiSection('claude', signal.claude_analysis, 'Claude AI (Anthropic)', c.blue)}

            {/* Divider */}
            <div style={{ borderTop: `1px solid ${c.border}`, margin: '2rem 0' }} />

            {/* ChatGPT Analysis */}
            {renderAiSection('chatgpt', signal.chatgpt_analysis, 'ChatGPT (OpenAI)', '#10b981')}

            {/* No analysis */}
            {!signal.claude_analysis && !signal.chatgpt_analysis && (
              <div style={{ textAlign: 'center', padding: '3rem', color: c.muted }}>
                No AI analysis available for this signal. AI analysis is generated during market hours.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
